import { formatMoneyValue } from '@utils/moneyFormatting';
import type {
  PortfolioTaxInsight,
  RunAdvisorInsights,
  RunSituationCount,
} from '../../types/advisorInsights.types';
import type { OptimizerAuditV2Record } from '../../types/audit.types';
import {
  AdvisorSituationKey,
  isCashEventNoteStage,
  OptimizerDecisionStage,
} from '../insights/advisorSituationKeys';
import type { RebalanceChangeSummary } from '../insights/buildRebalanceChangeSummary';

export type RunImpactTranslator = (
  key: string,
  options?: Record<string, unknown>,
) => string;

const DETAILED_TURNOVER_PORTFOLIO_THRESHOLD = 5;
const MIN_MEANINGFUL_TURNOVER_PCT = 1;

export { isCashEventNoteStage } from '../insights/advisorSituationKeys';

export const isCashDrivenRun = (insights: RunAdvisorInsights): boolean =>
  insights.situationCounts.some(
    (note) =>
      note.stage === AdvisorSituationKey.CashRaise ||
      note.stage === AdvisorSituationKey.CashDeploy,
  );

/** Cash badges duplicate trade-mix captions once the batch donut is visible. */
export const shouldHideRunCashEventFlags = (
  insights: RunAdvisorInsights,
  showTradeMix: boolean,
): boolean => showTradeMix && isCashDrivenRun(insights);

export const shouldShowRunTurnover = (
  insights: RunAdvisorInsights,
): boolean => {
  if (insights.turnoverMedianPct == null) {
    return false;
  }

  return Math.abs(insights.turnoverMedianPct) >= MIN_MEANINGFUL_TURNOVER_PCT;
};

export const shouldShowRunTradeMix = (
  insights: RunAdvisorInsights,
): boolean => {
  if (shouldShowRunTurnover(insights)) {
    return false;
  }

  if (!isCashDrivenRun(insights)) {
    return false;
  }

  const { totalBuyCents, totalSellCents } = insights.rollup;

  return totalBuyCents > 0 || totalSellCents > 0;
};

export type RunTradeMixMeterCopy = {
  buyCents: number;
  sellCents: number;
  centerLabel: string;
  caption: string;
  footnote: string;
};

export const formatRunTradeMixMeterCopy = (
  insights: RunAdvisorInsights,
  t: RunImpactTranslator,
): RunTradeMixMeterCopy | null => {
  if (!shouldShowRunTradeMix(insights)) {
    return null;
  }

  const buyCents = insights.rollup.totalBuyCents;
  const sellCents = insights.rollup.totalSellCents;
  const totalCents = buyCents + sellCents;

  if (totalCents <= 0) {
    return null;
  }

  const sellSharePct = (sellCents / totalCents) * 100;
  const buySharePct = (buyCents / totalCents) * 100;
  const hasRaise = insights.situationCounts.some(
    (note) => note.stage === AdvisorSituationKey.CashRaise,
  );
  const hasDeploy = insights.situationCounts.some(
    (note) => note.stage === AdvisorSituationKey.CashDeploy,
  );

  let caption = t('runInsights.impact.tradeMixMixedCaption');

  if (sellSharePct >= 60) {
    caption = t('runInsights.impact.tradeMixRaiseCaption');
  } else if (buySharePct >= 60) {
    caption = t('runInsights.impact.tradeMixDeployCaption');
  } else if (hasRaise && !hasDeploy) {
    caption = t('runInsights.impact.tradeMixRaiseCaption');
  } else if (hasDeploy && !hasRaise) {
    caption = t('runInsights.impact.tradeMixDeployCaption');
  }

  const dominantSharePct = Math.max(sellSharePct, buySharePct);

  return {
    buyCents,
    sellCents,
    centerLabel: `${Math.round(dominantSharePct)}%`,
    caption,
    footnote: t('runInsights.impact.tradeMixBuySell', {
      buys: formatMoneyValue(buyCents),
      sells: formatMoneyValue(sellCents),
    }),
  };
};

export const sumPortfolioTradeCents = (
  audit?: OptimizerAuditV2Record,
): { totalBuyCents: number; totalSellCents: number } => {
  const results = audit?.subAccountResults ?? [];

  return {
    totalBuyCents: results.reduce(
      (sum, result) => sum + Math.max(0, result.totalBuyCents ?? 0),
      0,
    ),
    totalSellCents: results.reduce(
      (sum, result) => sum + Math.max(0, result.totalSellCents ?? 0),
      0,
    ),
  };
};

export const shouldShowPortfolioTradeMix = (
  turnoverPct: number | null | undefined,
  isCashEvent: boolean,
  audit?: OptimizerAuditV2Record,
  taxInsight?: PortfolioTaxInsight | null,
  changes?: RebalanceChangeSummary,
): boolean => {
  if (shouldShowPortfolioTurnover(turnoverPct, isCashEvent)) {
    return false;
  }

  if (!isCashEvent) {
    return false;
  }

  if (changes?.hasMixedSleeveCash) {
    const { totalBuyCents, totalSellCents } = sumPortfolioTradeCents(audit);
    if (totalBuyCents === 0 || totalSellCents === 0) {
      return false;
    }
  }

  const { totalBuyCents, totalSellCents } = sumPortfolioTradeCents(audit);

  if (totalBuyCents <= 0 && totalSellCents <= 0) {
    return false;
  }

  // Sell-only raise: tax card already covers sell shape; skip redundant trade mix.
  if (totalBuyCents === 0 && totalSellCents > 0 && taxInsight?.hasSells) {
    return false;
  }

  return true;
};

export const formatPortfolioTradeMixMeterCopy = (
  audit: OptimizerAuditV2Record | undefined,
  changes: RebalanceChangeSummary,
  turnoverPct: number | null | undefined,
  t: RunImpactTranslator,
  taxInsight?: PortfolioTaxInsight | null,
): RunTradeMixMeterCopy | null => {
  if (
    !shouldShowPortfolioTradeMix(
      turnoverPct,
      changes.isCashEvent,
      audit,
      taxInsight,
      changes,
    )
  ) {
    return null;
  }

  const { totalBuyCents: buyCents, totalSellCents: sellCents } =
    sumPortfolioTradeCents(audit);
  const totalCents = buyCents + sellCents;

  if (totalCents <= 0) {
    return null;
  }

  const sellSharePct = (sellCents / totalCents) * 100;
  const buySharePct = (buyCents / totalCents) * 100;

  let caption = t('runInsights.impact.tradeMixMixedCaption');

  if (sellSharePct >= 60) {
    caption = t('runInsights.impact.tradeMixRaiseCaption');
  } else if (buySharePct >= 60) {
    caption = t('runInsights.impact.tradeMixDeployCaption');
  } else if (changes.cashRaiseCents > 0 && changes.cashDeployCents <= 0) {
    caption = t('runInsights.impact.tradeMixRaiseCaption');
  } else if (changes.cashDeployCents > 0 && changes.cashRaiseCents <= 0) {
    caption = t('runInsights.impact.tradeMixDeployCaption');
  }

  return {
    buyCents,
    sellCents,
    centerLabel: `${Math.round(Math.max(sellSharePct, buySharePct))}%`,
    caption,
    footnote: t('runInsights.impact.tradeMixBuySell', {
      buys: formatMoneyValue(buyCents),
      sells: formatMoneyValue(sellCents),
    }),
  };
};

export const buildPortfolioImpactSummaryParts = (
  taxInsight: PortfolioTaxInsight | null,
  turnoverPct: number | null | undefined,
  isCashEvent: boolean,
  t: RunImpactTranslator,
): string[] => {
  const parts: string[] = [];

  if (taxInsight?.taxableSellCents) {
    parts.push(
      t('insights.summaryTaxableSells', {
        value: formatMoneyValue(taxInsight.taxableSellCents),
      }),
    );
  } else if (taxInsight?.hasSells) {
    parts.push(t('insights.summaryShelteredSells'));
  }

  if (
    taxInsight?.estRealizedGainIsComplete &&
    taxInsight.estRealizedGainCents != null
  ) {
    parts.push(
      t('insights.summaryEstRealizedGains', {
        value: formatMoneyValue(taxInsight.estRealizedGainCents),
      }),
    );
  } else if (taxInsight && taxInsight.incompleteGainLineCount > 0) {
    if (
      taxInsight.estRealizedGainCents != null &&
      taxInsight.estRealizedGainCents !== 0
    ) {
      parts.push(
        t('insights.summaryEstRealizedGains', {
          value: formatMoneyValue(taxInsight.estRealizedGainCents),
        }),
      );
    }
    parts.push(
      t('insights.estRealizedGainsIncompleteLine', {
        count: taxInsight.incompleteGainLineCount,
      }),
    );
  } else if (
    taxInsight?.taxableSellCents &&
    taxInsight.estRealizedGainCents == null &&
    !taxInsight.estRealizedGainIsComplete
  ) {
    parts.push(t('insights.estRealizedGainsUnavailable'));
  }

  if (
    shouldShowPortfolioTurnover(turnoverPct, isCashEvent) &&
    turnoverPct != null
  ) {
    parts.push(t('insights.summaryTurnover', { value: turnoverPct }));
  }

  return parts;
};

export const buildPortfolioInsightsCollapsedPreview = (
  taxInsight: PortfolioTaxInsight | null,
  turnoverPct: number | null | undefined,
  changes: RebalanceChangeSummary,
  t: RunImpactTranslator,
): string[] => {
  const previewPartLimit = 3;

  // Triggers + interpretive impact only — cash raise/deploy amounts live in expanded
  // "What's changing" so we do not repeat them in the accordion subtitle.
  const parts: string[] = changes.hasMixedSleeveCash
    ? [t('insights.scopedCashPreview')]
    : [];

  const triggerLimit = previewPartLimit - parts.length;

  for (const trigger of changes.triggers.slice(0, triggerLimit)) {
    if (parts.length >= previewPartLimit) {
      break;
    }

    parts.push(
      t(
        `insights.trigger${trigger.charAt(0).toUpperCase()}${trigger.slice(1)}`,
      ),
    );
  }

  const impactParts = buildPortfolioImpactSummaryParts(
    taxInsight,
    turnoverPct,
    changes.isCashEvent,
    t,
  );

  for (const part of impactParts) {
    if (parts.length >= previewPartLimit) {
      break;
    }

    parts.push(part);
  }

  if (parts.length > 0) {
    return parts.slice(0, previewPartLimit);
  }

  if (changes.tradedHoldingCount > 0) {
    return [
      t('insights.broughtToTarget', { count: changes.tradedHoldingCount }),
    ];
  }

  return [];
};

export const shouldShowPortfolioTurnover = (
  turnoverPct: number | null | undefined,
  isCashEvent = false,
): boolean => {
  if (turnoverPct == null) {
    return false;
  }

  if (Math.abs(turnoverPct) < MIN_MEANINGFUL_TURNOVER_PCT) {
    return false;
  }

  if (isCashEvent && Math.abs(turnoverPct) < MIN_MEANINGFUL_TURNOVER_PCT) {
    return false;
  }

  return true;
};

export const partitionRunImpactNotes = (
  notes: RunSituationCount[],
): {
  cashEventNotes: RunSituationCount[];
  otherNotes: RunSituationCount[];
} => {
  const visible = filterVisibleRunImpactNotes(notes);

  return {
    cashEventNotes: visible.filter((note) => isCashEventNoteStage(note.stage)),
    otherNotes: visible.filter((note) => !isCashEventNoteStage(note.stage)),
  };
};

export const shouldShowDetailedTurnover = (
  insights: RunAdvisorInsights,
): boolean => {
  if (
    insights.portfoliosWithTurnover <= DETAILED_TURNOVER_PORTFOLIO_THRESHOLD
  ) {
    return false;
  }

  if (insights.turnoverMedianPct == null || insights.turnoverP90Pct == null) {
    return false;
  }

  return Math.abs(insights.turnoverMedianPct - insights.turnoverP90Pct) >= 0.05;
};

export const formatRunTurnoverLine = (
  insights: RunAdvisorInsights,
  t: RunImpactTranslator,
): string | null => {
  if (!shouldShowRunTurnover(insights)) {
    return null;
  }

  if (insights.turnoverMedianPct == null) {
    return null;
  }

  if (shouldShowDetailedTurnover(insights)) {
    return t('runInsights.impact.turnoverDetailed', {
      median: insights.turnoverMedianPct.toFixed(1),
      p90: insights.turnoverP90Pct?.toFixed(1),
    });
  }

  return t('runInsights.impact.turnoverTypical', {
    value: insights.turnoverMedianPct.toFixed(1),
  });
};

export type RunTurnoverMeterCopy = {
  caption: string;
  footnote: string;
  detailLine?: string;
};

export const formatRunTurnoverMeterCopy = (
  insights: RunAdvisorInsights,
  t: RunImpactTranslator,
): RunTurnoverMeterCopy | null => {
  if (!shouldShowRunTurnover(insights) || insights.turnoverMedianPct == null) {
    return null;
  }

  return {
    caption: t('runInsights.impact.turnoverRunCaption', {
      value: insights.turnoverMedianPct.toFixed(1),
    }),
    footnote: t('runInsights.impact.turnoverRunFootnote'),
    detailLine: shouldShowDetailedTurnover(insights)
      ? t('runInsights.impact.turnoverRunP90', {
          value: insights.turnoverP90Pct?.toFixed(1),
        })
      : undefined,
  };
};

const buildRunImpactHighlightParts = (
  insights: RunAdvisorInsights,
  t: RunImpactTranslator,
): string[] => {
  const parts: string[] = [];

  if (insights.taxableSellCents > 0) {
    parts.push(
      t('insights.summaryTaxableSells', {
        value: formatMoneyValue(insights.taxableSellCents),
      }),
    );
  } else if (insights.portfoliosWithSells > 0) {
    parts.push(t('insights.summaryShelteredSells'));
  }

  if (
    insights.estRealizedGainIsComplete &&
    insights.estRealizedGainCents != null
  ) {
    parts.push(
      t('insights.summaryEstRealizedGains', {
        value: formatMoneyValue(insights.estRealizedGainCents),
      }),
    );
  }

  const turnoverLine = formatRunTurnoverLine(insights, t);
  if (turnoverLine && insights.turnoverMedianPct != null) {
    parts.push(
      t('runInsights.impact.turnoverRunCaption', {
        value: insights.turnoverMedianPct.toFixed(1),
      }),
    );
  }

  return parts;
};

export const buildRunInsightsCollapsedPreview = (
  insights: RunAdvisorInsights | null,
  t: RunImpactTranslator,
  _portfolioSummary?: {
    failed?: number;
  } | null,
): string[] => {
  if (!insights) {
    return [];
  }

  // Collapsed line is the financial snapshot. Flags live in the expanded body
  // so they are not duplicated under the title when the card is open.
  const highlights = buildRunImpactHighlightParts(insights, t);
  if (highlights.length > 0) {
    return highlights.slice(0, 2);
  }

  const { cashEventNotes, otherNotes } = partitionRunImpactNotes(
    insights.situationCounts,
  );
  const flagParts = [
    ...cashEventNotes.map((note) => formatRunImpactNoteLine(note, t)),
    ...otherNotes.map((note) => formatRunImpactNoteLine(note, t)),
  ];

  return flagParts.slice(0, 3);
};

export const formatRunTaxImpactLines = (
  insights: RunAdvisorInsights,
  t: RunImpactTranslator,
): string[] => {
  if (insights.portfoliosWithSells <= 0) {
    return [];
  }

  if (insights.taxableSellCents <= 0) {
    return [t('runInsights.impact.shelteredOnly')];
  }

  const lines = [
    t('runInsights.impact.taxableSellsLine', {
      value: formatMoneyValue(insights.taxableSellCents),
      count: insights.portfoliosWithTaxableSells,
    }),
  ];

  if (
    insights.estRealizedGainIsComplete &&
    insights.estRealizedGainCents != null
  ) {
    lines.push(
      t('runInsights.impact.estRealizedGainsLine', {
        value: formatMoneyValue(insights.estRealizedGainCents),
        count: insights.portfoliosWithEstRealizedGain,
      }),
    );
  }

  return lines;
};

const RUN_IMPACT_NOTE_STAGES_HIDDEN_FROM_SUMMARY = new Set<string>([
  OptimizerDecisionStage.TaxLocation,
  OptimizerDecisionStage.FundingSegregation,
]);

export const formatRunImpactNoteLine = (
  note: RunSituationCount,
  t: RunImpactTranslator,
): string => {
  switch (note.stage) {
    case 'LIQUIDATION':
      return t('runInsights.impact.noteLiquidation', {
        count: note.portfolioCount,
      });
    case 'FX_DETECTION':
      return t('runInsights.impact.noteFx', {
        count: note.portfolioCount,
      });
    case 'SWITCH_DETECTION':
      return t('runInsights.impact.noteSwitch', {
        count: note.portfolioCount,
      });
    case 'COVARIANCE':
      return t('runInsights.impact.noteCovariance', {
        count: note.portfolioCount,
      });
    case AdvisorSituationKey.CashRaise:
      return t('runInsights.impact.noteCashRaise', {
        count: note.portfolioCount,
      });
    case AdvisorSituationKey.CashDeploy:
      return t('runInsights.impact.noteCashDeploy', {
        count: note.portfolioCount,
      });
    case AdvisorSituationKey.CashMode:
    case OptimizerDecisionStage.RebalanceMode:
      return t('runInsights.impact.noteCash', {
        count: note.portfolioCount,
      });
    case AdvisorSituationKey.MultiCurrency:
    case OptimizerDecisionStage.FundingSegregation:
      return t('runInsights.impact.noteMultiCurrency', {
        count: note.portfolioCount,
      });
    default:
      return t('runInsights.impact.noteDefault', {
        label: note.stage
          .split('_')
          .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
          .join(' '),
        count: note.portfolioCount,
      });
  }
};

export const filterVisibleRunImpactNotes = (
  notes: RunSituationCount[],
): RunSituationCount[] =>
  notes.filter(
    (note) => !RUN_IMPACT_NOTE_STAGES_HIDDEN_FROM_SUMMARY.has(note.stage),
  );
